import { useCallback, useState } from 'react';

export interface GoogleAuthConfig {
  clientId: string;
  scope?: string;
}

export interface GoogleSignInOptions {
  pairingKey?: string | null;
  figmaSource?: string | null;
  redirectTo?: string | null;
}

/**
 * Carried across the full-page redirect to Google and back via sessionStorage,
 * since a real navigation discards all in-memory React state (refs, closures).
 * `originPathname`/`originReferrer` capture what `window.location`/`document.referrer`
 * looked like on the page that triggered sign-in, since by the time the callback
 * page runs those have become the callback route and accounts.google.com.
 */
export interface PendingGoogleLogin extends GoogleSignInOptions {
  state: string;
  originPathname: string;
  originReferrer: string;
}

export const GOOGLE_OAUTH_PENDING_KEY = 'google_oauth_pending';
export const GOOGLE_OAUTH_CALLBACK_PATH = '/auth/google/callback';

const GOOGLE_OAUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';

interface UseGoogleAuthProps {
  clientId: string;
}

interface UseGoogleAuthReturn {
  isLoaded: boolean;
  isLoading: boolean;
  signInWithGoogle: (opts?: GoogleSignInOptions) => void;
  error: string | null;
}

/**
 * Triggers Google Sign-In via a full-page redirect (Authorization Code flow),
 * not a popup. `Cross-Origin-Opener-Policy: same-origin` — required for
 * WebContainer's `crossOriginIsolated` — severs `window.opener`, which breaks
 * popup-based sign-in on any page that also hosts the WebContainer.
 */
export function useGoogleAuth({ clientId }: UseGoogleAuthProps): UseGoogleAuthReturn {
  const [error, setError] = useState<string | null>(null);

  const signInWithGoogle = useCallback((opts?: GoogleSignInOptions) => {
    if (!clientId || clientId.includes('your-google-client-id')) {
      const errorMsg = 'Invalid Google Client ID';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setError(null);

    const state = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const pending: PendingGoogleLogin = {
      state,
      originPathname: window.location.pathname,
      originReferrer: document.referrer,
      pairingKey: opts?.pairingKey ?? null,
      figmaSource: opts?.figmaSource ?? null,
      redirectTo: opts?.redirectTo ?? null,
    };
    sessionStorage.setItem(GOOGLE_OAUTH_PENDING_KEY, JSON.stringify(pending));

    const redirectUri = `${window.location.origin}${GOOGLE_OAUTH_CALLBACK_PATH}`;
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      state,
      prompt: 'select_account',
    });

    window.location.href = `${GOOGLE_OAUTH_ENDPOINT}?${params.toString()}`;
  }, [clientId]);

  return {
    isLoaded: true,
    isLoading: false,
    signInWithGoogle,
    error,
  };
}
